export const DEFAULT_RESTAURANT_ID = '386632';
export const DEFAULT_HEIGHT = 630;
export const MIN_HEIGHT = 320;
export const MAX_HEIGHT = 1200;
export const DEFAULT_CTA_LABEL = 'Réserver une table';

export const BOOKINGS_ORIGIN = 'https://bookings.zenchef.com';
export const SDK_URL = 'https://sdk.zenchef.com/v1/sdk.min.js';
// The SDK looks for its own script tag by this id.
const SDK_SCRIPT_ID = 'zenchef-sdk';

// The widget id (pid) is the same for every restaurant on Zenchef.
const WIDGET_ID = '1001';

export type Display = 'embed' | 'button';

export interface Surface {
  // Hex color without the "#". The module paints its whole card with it.
  color: string;
  // Dark surfaces need the white version of the restaurant logo.
  whiteLogo: boolean;
}

// One surface per band style of the site, so the module blends into the
// band it sits on. Same values as the palette in theme/_variables.scss.
const SURFACES: Record<string, Surface> = {
  'accent-lin': { color: 'faf6ee', whiteLogo: false },
  'accent-pierre': { color: 'e4e1d7', whiteLogo: false },
  'accent-foret': { color: '38531c', whiteLogo: true },
  'accent-encre': { color: '2b2a24', whiteLogo: true },
};

// The page background (cream), for blocks without a band style.
const PAGE_SURFACE: Surface = { color: 'f1f1ef', whiteLogo: false };

// The brand green, for the module when it floats over the page.
export const BRAND_SURFACE: Surface = { color: '76947c', whiteLogo: true };

export function getDisplay(value: unknown): Display {
  return value === 'button' ? 'button' : 'embed';
}

// A Zenchef restaurant id is a number. Reject anything else so the iframe
// URL only ever contains digits.
export function normalizeRestaurantId(value: unknown): string {
  const id = String(value ?? '').trim();
  return /^\d+$/.test(id) ? id : '';
}

export function getSurface(styleName: unknown): Surface {
  return SURFACES[String(styleName ?? '')] ?? PAGE_SURFACE;
}

function surfaceParams(surface: Surface): Record<string, string> {
  return {
    lang: 'fr',
    hidelang: '1',
    primaryColor: surface.color,
    ...(surface.whiteLogo ? { white_logo: '1' } : {}),
  };
}

// The page shown inside the iframe. "fullscreen" makes the card fill the
// iframe, so no dark page background shows under it.
export function getZenchefWidgetUrl(
  restaurantId: string,
  surface: Surface,
): string {
  const params = new URLSearchParams({
    rid: restaurantId,
    pid: WIDGET_ID,
    ...surfaceParams(surface),
    fullscreen: '1',
  });
  return `${BOOKINGS_ORIGIN}/results?${params.toString()}`;
}

// The stand-alone booking page, for the button before the SDK is ready.
export function getBookingPageUrl(restaurantId: string): string {
  const params = new URLSearchParams({ rid: restaurantId, lang: 'fr' });
  return `${BOOKINGS_ORIGIN}/results?${params.toString()}`;
}

export function clampHeight(value: unknown): number {
  const height = Number(value);
  if (!Number.isFinite(height) || height <= 0) return DEFAULT_HEIGHT;
  return Math.min(MAX_HEIGHT, Math.max(MIN_HEIGHT, Math.round(height)));
}

// Load the Zenchef SDK once per page. It reads its options from the
// ".zc-widget-config" element and opens the module as an overlay.
export function ensureZenchefSdk(restaurantId: string): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(SDK_SCRIPT_ID)) return;

  const config = document.createElement('div');
  config.className = 'zc-widget-config';
  config.dataset.restaurant = restaurantId;
  config.dataset.lang = 'fr';
  config.dataset.hideLang = '1';
  config.dataset.primaryColor = BRAND_SURFACE.color;
  config.dataset.whiteLogo = '1';
  config.dataset.hideDefaultButton = 'true';
  config.dataset.disableGtm = 'true';
  config.dataset.disableGa4 = 'true';
  config.dataset.sendPageViews = 'false';
  document.body.appendChild(config);

  const script = document.createElement('script');
  script.id = SDK_SCRIPT_ID;
  script.src = SDK_URL;
  script.async = true;
  document.body.appendChild(script);
}

export function openZenchefWidget(): boolean {
  const widget = (window as any).ZenchefWidget;
  if (typeof widget?.open !== 'function') return false;
  widget.open();
  return true;
}
